import { AccessDeniedError, accessDenied, must } from "@corpuscomplete/core/utils";
import type { UserActor } from "@corpuscomplete/core/actor";
import type { ExpressionBuilder, Query, QueryRowType, Row, Transaction } from "@rocicorp/zero";
import { z } from "zod/v4";

import { Group } from "../../auth/group";
import { AccessGrant } from "../../auth/grant";
import { Scope } from "../../auth/scope";
import { ScopePolicy } from "../../auth/scope/policy";
import { DateRange } from "../../common/date-time";
import { temporalInstantSchema } from "../../common/date-time/zod";
import { acquireAdvisoryLockIfServer } from "../../drizzle/advisory-lock";
import { zTimezone } from "../../utils/form-schema";
import { assertValidIdChanges } from "../../utils/id-change";
import { createInsertSchema, createUpdateSchema } from "../../utils/sql";
import { zql, type Schema } from "../../zero/schema";
import { defineQuery } from "../../zero/transformer";
import { withZeroTransaction } from "../../zero/utils";
import { Food } from "../food";
import type { FoodLog } from "../food-log";
import { FoodItemMatcher } from "../food-matcher";
import { MealPlanningSessionScopePolicy } from "../meal-planning-session/scope-policy";
import {
  plannedMealEventAssignmentV2,
  plannedMealEventV2,
  plannedMealPlateItemDescriptorV2,
  plannedMealPlateItemV2,
  plannedMealPlateV2,
  plannedMealV2,
} from "./planned-meal.sql";
import { Nutrient } from "../nutrient";

export namespace PlannedMeal {
  const MAX_NAME_LENGTH = 255;

  const removeByIdSchema = z.object({ id: z.uuid() });

  const plateItemSchemas = {
    add: createInsertSchema(plannedMealPlateItemV2).omit({ mealId: true, plateId: true }),
    edit: createUpdateSchema(plannedMealPlateItemV2)
      .omit({ mealId: true, plateId: true })
      .extend({ id: z.uuid() }),
  };

  const plateItemDescriptorSchemas = {
    add: createInsertSchema(plannedMealPlateItemDescriptorV2)
      .omit({ mealId: true, plateId: true })
      .extend({
        data: FoodItemMatcher.ItemDescriptor,
      }),
    edit: createUpdateSchema(plannedMealPlateItemDescriptorV2)
      .omit({ mealId: true, plateId: true })
      .extend({
        id: z.uuid(),
        data: FoodItemMatcher.ItemDescriptor.optional(),
      }),
  };

  const plateSchemas = {
    add: createInsertSchema(plannedMealPlateV2).omit({ mealId: true }).extend({
      nutrientTargets: Nutrient.targetsSchema().nullable(),
      items: plateItemSchemas.add.array(),
      itemDescriptors: plateItemDescriptorSchemas.add.array(),
    }),
    edit: createUpdateSchema(plannedMealPlateV2)
      .omit({ mealId: true, userId: true })
      .extend({
        id: z.uuid(),
        nutrientTargets: Nutrient.targetsSchema().nullable().optional(),
        items: z
          .object({
            add: plateItemSchemas.add.array().optional(),
            edit: plateItemSchemas.edit.array().optional(),
            remove: removeByIdSchema.array().optional(),
          })
          .optional(),
        itemDescriptors: z
          .object({
            add: plateItemDescriptorSchemas.add.array().optional(),
            edit: plateItemDescriptorSchemas.edit.array().optional(),
            remove: removeByIdSchema.array().optional(),
          })
          .optional(),
      }),
  };

  const assignmentSchemas = {
    add: createInsertSchema(plannedMealEventAssignmentV2).omit({
      mealId: true,
      eventId: true,
      userId: true,
    }),
  };

  const eventSchemas = {
    add: createInsertSchema(plannedMealEventV2)
      .omit({ mealId: true })
      .extend({
        timeAt: temporalInstantSchema,
        assignments: assignmentSchemas.add.array().default([]),
      }),
    edit: createUpdateSchema(plannedMealEventV2)
      .omit({ mealId: true })
      .extend({
        id: z.uuid(),
        timeAt: temporalInstantSchema.optional(),
        assignments: z
          .object({
            add: assignmentSchemas.add.array().optional(),
            remove: removeByIdSchema.array().optional(),
          })
          .optional(),
      }),
  };

  export type CreateDraft = z.infer<typeof createDraft.schema>;
  export const createDraft = withZeroTransaction(
    createInsertSchema(plannedMealV2)
      .omit({ status: true })
      .extend({
        typeLabel: z.string().trim().min(1),
        name: z.string().trim().max(MAX_NAME_LENGTH),
        events: eventSchemas.add.array().default([]),
        plates: plateSchemas.add.array(),
      }),
    async ({ tx, ctx, args }) => {
      await createDrafts.fn({
        tx,
        ctx,
        args: {
          scopeId: args.scopeId,
          meals: [
            {
              id: args.id,
              typeLabel: args.typeLabel,
              name: args.name,
              events: args.events,
              plates: args.plates,
            },
          ],
        },
      });
    },
  );

  export type CreateDrafts = z.infer<typeof createDrafts.schema>;
  export const createDrafts = withZeroTransaction(
    z.object({
      scopeId: z.uuid(),
      meals: z.array(createDraft.schema.omit({ scopeId: true })),
    }),
    async ({ tx, ctx, args }) => {
      const access = await requireDraftCreateContext(tx, ctx.actor, args.scopeId);
      await createDraftRows(tx, ctx.actor, args, access);
    },
  );

  export async function createDraftsForAllowedPlateUsers(
    tx: Transaction,
    actor: UserActor,
    args: {
      scopeId: string;
      meals: CreateDrafts["meals"];
      allowedPlateUserIds: readonly string[];
    },
  ): Promise<void> {
    await createDraftRows(tx, actor, args, {
      kind: "allowedUsers",
      location: "server",
      userIds: args.allowedPlateUserIds,
      message: "Session-origin plates must target session members",
    });
  }

  async function createDraftRows(
    tx: Transaction,
    actor: UserActor,
    args: CreateDrafts,
    access: PlannedMealDraftContext,
  ): Promise<void> {
    assertUniqueIdsByLabel(
      "meal",
      args.meals.map((meal) => meal.id),
    );
    const inputMeals = args.meals.map((meal) => ({
      ...meal,
      events: (meal.events ?? []).map((event) => ({
        ...event,
        assignments: event.assignments ?? [],
      })),
    }));
    assertUniqueIdsByLabel(
      "plate",
      inputMeals.flatMap((meal) => meal.plates.map((plate) => plate.id)),
    );
    assertUniqueIdsByLabel(
      "plate item",
      inputMeals.flatMap((meal) =>
        meal.plates.flatMap((plate) => plate.items.map((item) => item.id)),
      ),
    );
    assertUniqueIdsByLabel(
      "plate item descriptor",
      inputMeals.flatMap((meal) =>
        meal.plates.flatMap((plate) => plate.itemDescriptors.map((descriptor) => descriptor.id)),
      ),
    );
    assertUniqueIdsByLabel(
      "meal event",
      inputMeals.flatMap((meal) => meal.events.map((event) => event.id)),
    );
    assertUniqueIdsByLabel(
      "meal event assignment",
      inputMeals.flatMap((meal) =>
        meal.events.flatMap((event) => event.assignments.map((assignment) => assignment.id)),
      ),
    );

    const meals = inputMeals.map((meal) => ({
      ...meal,
      plates: meal.plates.map(normalizePlate),
    }));
    for (const meal of meals) {
      validatePlateState(meal.plates);
    }
    await assertPlateUsersAllowed(
      tx,
      access,
      meals.flatMap((meal) => meal.plates.map((plate) => plate.userId)),
    );
    await assertPlateServingReferencesViewable(
      tx,
      actor,
      meals.flatMap((meal) => meal.plates),
    );

    const timeNow = Temporal.Now.instant();
    for (const meal of meals) {
      await tx.mutate.plannedMealV2.insert({
        id: meal.id,
        scopeId: args.scopeId,
        typeLabel: meal.typeLabel,
        status: "draft",
        name: meal.name,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });

      await Promise.all(meal.plates.map((plate) => addPlateInternal(tx, meal.id, plate, timeNow)));
      await Promise.all(meal.events.map((event) => addEventInternal(tx, meal.id, event, timeNow)));
      await Promise.all(
        meal.events.flatMap((event) =>
          (event.assignments ?? []).map((assignment) =>
            addAssignmentInternal(
              tx,
              meal.id,
              { ...assignment, eventId: event.id },
              meal.plates,
              timeNow,
            ),
          ),
        ),
      );
    }
  }

  export type EditDraft = z.infer<typeof editDraft.schema>;
  export const editDraft = withZeroTransaction(
    createUpdateSchema(plannedMealV2)
      .omit({ scopeId: true, status: true })
      .extend({
        id: z.uuid(),
        typeLabel: z.string().trim().min(1).optional(),
        name: z.string().max(MAX_NAME_LENGTH).optional(),
        plates: z
          .object({
            add: plateSchemas.add.array().optional(),
            edit: plateSchemas.edit.array().optional(),
            remove: removeByIdSchema.array().optional(),
          })
          .optional(),
        events: z
          .object({
            add: eventSchemas.add.array().optional(),
            edit: eventSchemas.edit.array().optional(),
            remove: removeByIdSchema.array().optional(),
          })
          .optional(),
      }),
    async ({ tx, ctx, args }) => {
      const { access, info: existingInfo } = await requireWritableDraftWithInfo(
        tx,
        ctx.actor,
        args.id,
      );

      const nextPlates = existingInfo.plates.map(toEditablePlate);
      if (args.plates) {
        assertValidIdChanges(nextPlates, args.plates, {
          operation: "PlannedMeal.editDraft",
          label: "plate",
        });

        const removedIds = new Set(args.plates.remove?.map((entry) => entry.id) ?? []);
        const keptPlates = nextPlates.filter((plate) => !removedIds.has(plate.id));
        const nextById = mapById(keptPlates);

        for (const plateEdit of args.plates.edit ?? []) {
          const plate = nextById.get(plateEdit.id);
          if (!plate) {
            throw new Error("PlannedMeal.editDraft cannot edit missing plate");
          }
          nextById.set(plate.id, projectPlateEdit(plate, plateEdit));
        }

        nextPlates.splice(
          0,
          nextPlates.length,
          ...keptPlates.map((plate) => nextById.get(plate.id) ?? plate),
          ...(args.plates.add ?? []).map((plate) => normalizePlate(plate)),
        );
      }
      validatePlateState(nextPlates);
      await assertPlateUsersAllowed(
        tx,
        access,
        nextPlates.map((plate) => plate.userId),
      );
      await assertPlateServingReferencesViewable(tx, ctx.actor, nextPlates);

      const timeNow = Temporal.Now.instant();
      await tx.mutate.plannedMealV2.update({
        id: args.id,
        ...(args.typeLabel !== undefined ? { typeLabel: args.typeLabel } : {}),
        ...(args.name !== undefined ? { name: args.name } : {}),
        timeUpdated: timeNow,
      });
      if (args.events) {
        validateEventAssignmentChanges(existingInfo, args.events);
      }

      if (args.plates) {
        const platesById = mapById(existingInfo.plates.map(toEditablePlate));
        await Promise.all(
          (args.plates.remove ?? []).map(async ({ id }) => {
            const plate = platesById.get(id);
            if (!plate) {
              throw new Error("PlannedMeal.editDraft cannot remove missing plate");
            }
            await removePlateInternal(tx, plate);
          }),
        );
        await Promise.all(
          (args.plates.edit ?? []).map(async (plateEdit) => {
            const plate = platesById.get(plateEdit.id);
            if (!plate) {
              throw new Error("PlannedMeal.editDraft cannot edit missing plate");
            }
            await editPlateInternal(tx, args.id, plate, plateEdit, timeNow);
          }),
        );
        await Promise.all(
          (args.plates.add ?? []).map((plateAdd) =>
            addPlateInternal(tx, args.id, normalizePlate(plateAdd), timeNow),
          ),
        );
      }

      if (args.events) {
        const changes = args.events;
        assertValidIdChanges(existingInfo.events, changes, {
          operation: "PlannedMeal.editDraft",
          label: "meal event",
        });
        const eventsById = mapById(existingInfo.events);
        await Promise.all(
          (changes.remove ?? []).map(({ id }) =>
            removeEventInternal(
              tx,
              must(eventsById.get(id), "PlannedMeal.editDraft cannot remove missing meal event"),
            ),
          ),
        );
        await Promise.all(
          (changes.edit ?? []).map(async (eventEdit) => {
            const event = must(
              eventsById.get(eventEdit.id),
              "PlannedMeal.editDraft cannot edit missing meal event",
            );
            await editEventInternal(tx, event, eventEdit, timeNow);
            await editEventAssignmentsInternal(tx, args.id, event, eventEdit, nextPlates, timeNow);
          }),
        );
        await Promise.all(
          (changes.add ?? []).map(async (event) => {
            await addEventInternal(tx, args.id, event, timeNow);
            await Promise.all(
              (event.assignments ?? []).map((assignment) =>
                addAssignmentInternal(
                  tx,
                  args.id,
                  { ...assignment, eventId: event.id },
                  nextPlates,
                  timeNow,
                ),
              ),
            );
          }),
        );
      }
    },
  );

  export type DeleteDraft = z.infer<typeof deleteDraft.schema>;
  export const deleteDraft = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args }) => {
      const { info } = await requireWritableDraftWithInfo(tx, ctx.actor, args.id);
      const sessionLinks = await tx.run(
        zql.mealPlanningSessionPlannedMeal.where("plannedMealId", "=", args.id),
      );
      await Promise.all(
        sessionLinks.map((link) =>
          tx.mutate.mealPlanningSessionPlannedMeal.delete({
            mealPlanningSessionId: link.mealPlanningSessionId,
            plannedMealId: link.plannedMealId,
          }),
        ),
      );
      await Promise.all(info.events.map((event) => removeEventInternal(tx, event)));
      await Promise.all(
        info.plates.map((plate) => removePlateInternal(tx, toEditablePlate(plate))),
      );
      await tx.mutate.plannedMealV2.delete({ id: args.id });
    },
  );

  export type Publish = z.infer<typeof publish.schema>;
  export const publish = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args }) => {
      const { info } = await requireWritableDraftWithInfo(tx, ctx.actor, args.id);
      const timeNow = Temporal.Now.instant();
      await publishDraft(tx, { id: info.id, timeNow });
    },
  );

  export type Unpublish = z.infer<typeof unpublish.schema>;
  export const unpublish = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args }) => {
      await lockPlannedMeal(tx, args.id, "exclusive");
      const { meal } = await requireWritableMeal(tx, ctx.actor, args.id);
      if (meal.status !== "published") {
        throw new Error("Only published planned meals can be unpublished");
      }
      const assignments = await tx.run(
        zql.plannedMealEventAssignmentV2.where("mealId", "=", args.id),
      );
      const linkedSource = assignments.length
        ? await tx.run(
            zql.foodLogSourceGroup
              .where("sourceType", "=", "planned_meal_event_assignment_v2")
              .where(
                "sourceId",
                "IN",
                assignments.map((assignment) => assignment.id),
              )
              .one(),
          )
        : null;
      if (linkedSource) {
        throw new Error("Cannot unpublish a planned meal with linked food logs");
      }
      await tx.mutate.plannedMealV2.update({
        id: args.id,
        status: "draft",
        timeUpdated: Temporal.Now.instant(),
      });
    },
  );

  export type AddPlate = z.infer<typeof addPlate.schema>;
  export const addPlate = withZeroTransaction(
    plateSchemas.add.extend({ mealId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, ...plate } }) => {
      const { access, info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const normalizedPlate = normalizePlate(plate);
      const nextPlates = [...info.plates.map(toEditablePlate), normalizedPlate];
      await validatePlatesForMeal(tx, ctx.actor, access, nextPlates);

      const timeNow = Temporal.Now.instant();
      await addPlateInternal(tx, info.id, normalizedPlate, timeNow);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type EditPlate = z.infer<typeof editPlate.schema>;
  export const editPlate = withZeroTransaction(
    plateSchemas.edit.extend({ mealId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, ...plate } }) => {
      const { access, info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const plates = info.plates.map(toEditablePlate);
      const current = must(
        mapById(plates).get(plate.id),
        "PlannedMeal.editPlate cannot edit missing plate",
      );
      const nextPlate = projectPlateEdit(current, plate);
      const nextPlates = plates.map((candidate) =>
        candidate.id === plate.id ? nextPlate : candidate,
      );
      await validatePlatesForMeal(tx, ctx.actor, access, nextPlates);

      const timeNow = Temporal.Now.instant();
      await editPlateInternal(tx, info.id, current, plate, timeNow);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type RemovePlate = z.infer<typeof removePlate.schema>;
  export const removePlate = withZeroTransaction(
    removeByIdSchema.extend({ mealId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, id } }) => {
      const { access, info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const plates = info.plates.map(toEditablePlate);
      const current = must(
        mapById(plates).get(id),
        "PlannedMeal.removePlate cannot remove missing plate",
      );
      const nextPlates = plates.filter((plate) => plate.id !== id);
      await validatePlatesForMeal(tx, ctx.actor, access, nextPlates);

      const timeNow = Temporal.Now.instant();
      await removePlateInternal(tx, current);
      await touchPlannedMeal(tx, mealId, timeNow);
    },
  );

  export type AddEvent = z.infer<typeof addEvent.schema>;
  export const addEvent = withZeroTransaction(
    eventSchemas.add.extend({ mealId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, ...event } }) => {
      const { info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const timeNow = Temporal.Now.instant();
      await addEventInternal(tx, info.id, event, timeNow);
      await Promise.all(
        (event.assignments ?? []).map((assignment) =>
          addAssignmentInternal(
            tx,
            info.id,
            { ...assignment, eventId: event.id },
            info.plates,
            timeNow,
          ),
        ),
      );
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type EditEvent = z.infer<typeof editEvent.schema>;
  export const editEvent = withZeroTransaction(
    eventSchemas.edit.extend({ mealId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, ...event } }) => {
      const { info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const current = must(
        mapById(info.events).get(event.id),
        "PlannedMeal.editEvent cannot edit missing event",
      );
      const timeNow = Temporal.Now.instant();
      await editEventInternal(tx, current, event, timeNow);
      await editEventAssignmentsInternal(tx, info.id, current, event, info.plates, timeNow);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type RemoveEvent = z.infer<typeof removeEvent.schema>;
  export const removeEvent = withZeroTransaction(
    removeByIdSchema.extend({ mealId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, id } }) => {
      const { info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const current = must(
        mapById(info.events).get(id),
        "PlannedMeal.removeEvent cannot remove missing event",
      );
      const timeNow = Temporal.Now.instant();
      await removeEventInternal(tx, current);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type AddAssignment = z.infer<typeof addAssignment.schema>;
  export const addAssignment = withZeroTransaction(
    assignmentSchemas.add.extend({ mealId: z.uuid(), eventId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, ...assignment } }) => {
      const { info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const timeNow = Temporal.Now.instant();
      await addAssignmentInternal(tx, info.id, assignment, info.plates, timeNow);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type RemoveAssignment = z.infer<typeof removeAssignment.schema>;
  export const removeAssignment = withZeroTransaction(
    removeByIdSchema.extend({ mealId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, id } }) => {
      const { info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const current = must(
        mapById(info.assignments).get(id),
        "PlannedMeal.removeAssignment cannot remove missing assignment",
      );
      const timeNow = Temporal.Now.instant();
      await removeAssignmentInternal(tx, current);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type AddPlateItem = z.infer<typeof addPlateItem.schema>;
  export const addPlateItem = withZeroTransaction(
    plateItemSchemas.add.extend({ mealId: z.uuid(), plateId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, plateId, ...item } }) => {
      const { access, info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const { plates, current, nextPlate } = projectPlateChange(info, plateId, {
        id: plateId,
        items: { add: [item] },
      });
      await validatePlatesForMeal(
        tx,
        ctx.actor,
        access,
        plates.map((plate) => (plate.id === plateId ? nextPlate : plate)),
      );

      const timeNow = Temporal.Now.instant();
      await addPlateItemInternal(tx, info.id, current.id, item, timeNow);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type EditPlateItem = z.infer<typeof editPlateItem.schema>;
  export const editPlateItem = withZeroTransaction(
    plateItemSchemas.edit.extend({ mealId: z.uuid(), plateId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, plateId, ...item } }) => {
      const { access, info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const { plates, nextPlate } = projectPlateChange(info, plateId, {
        id: plateId,
        items: { edit: [item] },
      });
      await validatePlatesForMeal(
        tx,
        ctx.actor,
        access,
        plates.map((plate) => (plate.id === plateId ? nextPlate : plate)),
      );

      const timeNow = Temporal.Now.instant();
      await editPlateItemInternal(tx, item, timeNow);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type RemovePlateItem = z.infer<typeof removePlateItem.schema>;
  export const removePlateItem = withZeroTransaction(
    removeByIdSchema.extend({ mealId: z.uuid(), plateId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, plateId, id } }) => {
      const { access, info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const { plates, nextPlate } = projectPlateChange(info, plateId, {
        id: plateId,
        items: { remove: [{ id }] },
      });
      await validatePlatesForMeal(
        tx,
        ctx.actor,
        access,
        plates.map((plate) => (plate.id === plateId ? nextPlate : plate)),
      );

      const timeNow = Temporal.Now.instant();
      await removePlateItemInternal(tx, id);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type AddPlateItemDescriptor = z.infer<typeof addPlateItemDescriptor.schema>;
  export const addPlateItemDescriptor = withZeroTransaction(
    plateItemDescriptorSchemas.add.extend({ mealId: z.uuid(), plateId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, plateId, ...descriptor } }) => {
      const { access, info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const { plates, current, nextPlate } = projectPlateChange(info, plateId, {
        id: plateId,
        itemDescriptors: { add: [descriptor] },
      });
      await validatePlatesForMeal(
        tx,
        ctx.actor,
        access,
        plates.map((plate) => (plate.id === plateId ? nextPlate : plate)),
      );

      const timeNow = Temporal.Now.instant();
      await addPlateItemDescriptorInternal(tx, info.id, current.id, descriptor, timeNow);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type EditPlateItemDescriptor = z.infer<typeof editPlateItemDescriptor.schema>;
  export const editPlateItemDescriptor = withZeroTransaction(
    plateItemDescriptorSchemas.edit.extend({ mealId: z.uuid(), plateId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, plateId, ...descriptor } }) => {
      const { access, info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const { plates, nextPlate } = projectPlateChange(info, plateId, {
        id: plateId,
        itemDescriptors: { edit: [descriptor] },
      });
      await validatePlatesForMeal(
        tx,
        ctx.actor,
        access,
        plates.map((plate) => (plate.id === plateId ? nextPlate : plate)),
      );

      const timeNow = Temporal.Now.instant();
      await editPlateItemDescriptorInternal(tx, descriptor, timeNow);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export type RemovePlateItemDescriptor = z.infer<typeof removePlateItemDescriptor.schema>;
  export const removePlateItemDescriptor = withZeroTransaction(
    removeByIdSchema.extend({ mealId: z.uuid(), plateId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, plateId, id } }) => {
      const { access, info } = await requireWritableDraftWithInfo(tx, ctx.actor, mealId);
      const { plates, nextPlate } = projectPlateChange(info, plateId, {
        id: plateId,
        itemDescriptors: { remove: [{ id }] },
      });
      await validatePlatesForMeal(
        tx,
        ctx.actor,
        access,
        plates.map((plate) => (plate.id === plateId ? nextPlate : plate)),
      );

      const timeNow = Temporal.Now.instant();
      await removePlateItemDescriptorInternal(tx, id);
      await touchPlannedMeal(tx, info.id, timeNow);
    },
  );

  export const withRelatedMany = (query: Query<"plannedMealV2", Schema>) =>
    query
      .related("plates", (plate) =>
        plate
          .related("items", (item) =>
            item
              .related("serving", (serving) =>
                serving.related("food", (food) => food.related("meta")),
              )
              .orderBy("sortIndex", "asc"),
          )
          .related("itemDescriptors", (descriptor) => descriptor.orderBy("sortIndex", "asc")),
      )
      .related("events", (event) =>
        event
          .related("assignments", (assignment) =>
            assignment.related("foodLogSourceGroup").orderBy("userId", "asc"),
          )
          .orderBy("timeAt", "asc"),
      );

  const withRelated = (query: Query<"plannedMealV2", Schema>) => withRelatedMany(query).one();

  function viewable(eb: ExpressionBuilder<"plannedMealV2", Schema>, actor: UserActor) {
    return eb.or(
      eb.cmp("scopeId", "=", actor.userId),
      eb.exists(
        "scope",
        (scope) =>
          scope.where((candidate) => ScopePolicy.viewable(candidate, actor, ["user", "space"])),
        { flip: true },
      ),
    );
  }

  export const byId = defineQuery(z.object({ id: z.uuid() }), ({ ctx, args }) =>
    withRelated(
      zql.plannedMealV2
        .where("id", "=", args.id)
        .where((candidate) => viewable(candidate, ctx.actor)),
    ),
  );

  export const eventsInDateRange = defineQuery(
    z.object({
      scopeId: z.uuid(),
      dateRange: DateRange.closedSchema,
      timeZone: zTimezone(),
    }),
    ({ ctx, args }) => {
      const timeStart = args.dateRange.dateStart
        .toZonedDateTime({ timeZone: args.timeZone, plainTime: Temporal.PlainTime.from("00:00") })
        .toInstant();
      const timeEnd = args.dateRange.dateEnd
        .add({ days: 1 })
        .toZonedDateTime({ timeZone: args.timeZone, plainTime: Temporal.PlainTime.from("00:00") })
        .toInstant();

      return withRelatedMany(
        zql.plannedMealV2
          .where("scopeId", "=", args.scopeId)
          .where((candidate) => viewable(candidate, ctx.actor))
          .whereExists("events", (event) =>
            event.where("timeAt", ">=", timeStart).where("timeAt", "<", timeEnd),
          ),
      ).orderBy("timeCreated", "asc");
    },
  );

  export const withoutEventsByScope = defineQuery(
    z.object({
      scopeId: z.uuid(),
      limit: z.number().int().positive().max(100).default(50),
      cursor: z.uuid().optional(),
    }),
    ({ ctx, args }) => {
      let query = withRelatedMany(
        zql.plannedMealV2
          .where("scopeId", "=", args.scopeId)
          .where("status", "=", "draft")
          .where((candidate) => viewable(candidate, ctx.actor))
          .where((eb) => eb.not(eb.exists("events", (event) => event))),
      ).orderBy("id", "asc");
      if (args.cursor) {
        query = query.where("id", ">", args.cursor);
      }
      return query.limit(args.limit);
    },
  );

  export const eventAssignmentsInDateRange = defineQuery(
    z.object({ userId: z.uuid(), dateRange: DateRange.closedSchema, timeZone: zTimezone() }),
    ({ ctx, args }) => {
      if (args.userId !== ctx.actor.userId) {
        accessDenied("Cannot view another user's planned meal assignments");
      }
      const timeStart = args.dateRange.dateStart
        .toZonedDateTime({ timeZone: args.timeZone, plainTime: Temporal.PlainTime.from("00:00") })
        .toInstant();
      const timeEnd = args.dateRange.dateEnd
        .add({ days: 1 })
        .toZonedDateTime({ timeZone: args.timeZone, plainTime: Temporal.PlainTime.from("00:00") })
        .toInstant();

      return withRelatedMany(
        zql.plannedMealV2
          .where("status", "=", "published")
          .where((candidate) => viewable(candidate, ctx.actor))
          .whereExists("events", (event) =>
            event
              .where("timeAt", ">=", timeStart)
              .where("timeAt", "<", timeEnd)
              .whereExists("assignments", (assignment) =>
                assignment.where("userId", "=", args.userId),
              ),
          ),
      ).orderBy("timeCreated", "asc");
    },
  );

  export const foodLogSourceAdapter: FoodLog.SourceAdapter = {
    buildEntries: async (tx, actor, sourceId, userId, entryIds) => {
      const assignment = await tx.run(
        zql.plannedMealEventAssignmentV2
          .where("id", "=", sourceId)
          .related("event", (event) =>
            event.related("plannedMeal", (meal) =>
              meal.where((candidate) => viewable(candidate, actor)),
            ),
          )
          .related("plate", (plate) =>
            plate.related("items", (item) => item.orderBy("sortIndex", "asc")),
          )
          .one(),
      );
      if (!assignment?.event?.plannedMeal) {
        accessDenied("Cannot view planned meal assignment");
      }

      const meal = assignment.event.plannedMeal;
      const plate = assignment.plate;
      if (!plate) {
        accessDenied("Cannot create a source-backed food log without a planned meal plate");
      }

      if (assignment.userId !== actor.userId) {
        accessDenied("Cannot create a source-backed food log for a different user");
      }

      if (assignment.userId !== userId) {
        accessDenied("Cannot create a source-backed food log for a different user");
      }

      if (meal.status !== "published") {
        accessDenied("Cannot create a source-backed food log from an unpublished planned meal");
      }

      if (entryIds.length !== plate.items.length) {
        accessDenied(
          "Cannot create a source-backed food log with a different number of entries than the planned meal assignment",
        );
      }

      return plate.items.map((item, index) => ({
        id: must(entryIds[index]),
        foodServingId: item.foodServingId,
        amount: item.amount,
      }));
    },
  };

  export function foodLogSourceGroupContribution(
    plannedMeals: readonly Info[],
    date: Temporal.PlainDate,
    timeZone: string,
    userId: string,
  ): FoodLog.SourceGroupContribution {
    const defaultLabel = "Planned meal";
    const candidates: FoodLog.SourceGroupCandidateLike[] = [];
    const sources = new Map<string, FoodLog.Source>();

    for (const meal of plannedMeals) {
      if (meal.status !== "published") {
        continue;
      }
      for (const event of meal.events) {
        for (const assignment of event.assignments) {
          if (assignment.userId !== userId) {
            continue;
          }
          const plate = meal.plates.find((candidate) => candidate.id === assignment.plateId);
          if (!plate) {
            continue;
          }
          const sourceTimeAt = assignment.foodLogSourceGroup?.timeAt ?? event.timeAt;
          const assignmentDate = sourceTimeAt.toZonedDateTimeISO(timeZone).toPlainDate();
          if (!assignmentDate.equals(date)) {
            continue;
          }

          const label = `${meal.typeLabel || defaultLabel} - ${meal.name || defaultLabel}`;
          if (assignment.foodLogSourceGroup) {
            sources.set(assignment.id, {
              label,
              items: plate.items,
              status: "resolved",
            });
            continue;
          }

          candidates.push({
            kind: "sourceGroupCandidate",
            id: assignment.id,
            timeAt: event.timeAt,
            label,
            items: plate.items,
            sourceId: assignment.id,
            sourceType: "planned_meal_event_assignment_v2",
          });
          continue;
        }
      }
    }

    return { candidates, sources, defaultLabel };
  }

  type RawInfo = NonNullable<QueryRowType<typeof withRelated>>;
  type PlateItem = Food.AsItem<RawInfo["plates"][number]["items"][number]> & {
    id: RawInfo["plates"][number]["items"][number]["id"];
    foodServingId: RawInfo["plates"][number]["items"][number]["foodServingId"];
    sortIndex: RawInfo["plates"][number]["items"][number]["sortIndex"];
  };
  type Plate = Omit<RawInfo["plates"][number], "items" | "nutrientTargets"> & {
    items: PlateItem[];
    nutrientTargets: Nutrient.Targets | null;
  };
  type Event = RawInfo["events"][number];
  type Assignment = RawInfo["events"][number]["assignments"][number];
  type EditablePlate = Pick<RawInfo["plates"][number], "id" | "userId"> & {
    nutrientTargets: Nutrient.Targets | null;
    items: Pick<
      RawInfo["plates"][number]["items"][number],
      "id" | "foodServingId" | "amount" | "sortIndex"
    >[];
    itemDescriptors: Pick<
      RawInfo["plates"][number]["itemDescriptors"][number],
      "id" | "data" | "sortIndex"
    >[];
  };
  export type Info = Omit<RawInfo, "events" | "plates"> & {
    plates: Plate[];
    events: Event[];
    assignments: Assignment[];
  };
  export type Status = Info["status"];
  type MealRow = Row<Schema["tables"]["plannedMealV2"]>;
  type ParentScope = Scope.Info<"user" | "space">;
  type PlannedMealDraftContext =
    | { kind: "manual"; location: "server"; scope: ParentScope }
    | { kind: "manual"; location: "client" }
    | { kind: "allowedUsers"; location: "server"; userIds: readonly string[]; message: string }
    | { kind: "mealPlanningSession"; location: "server"; mealPlanningSessionId: string };

  export type PublishViolationTarget =
    | { entity: "plannedMeal"; code: "emptyName" | "noPlates" | "noEvents" }
    | { entity: "event"; id: string; code: "noAssignments" }
    | { entity: "plate"; id: string; code: "noItems" | "hasDescriptors" | "noAssignments" };

  export type PublishViolations = {
    plannedMeal: {
      emptyName?: true;
      noPlates?: true;
      noEvents?: true;
    };
    events: Record<string, { noAssignments?: true }>;
    plates: Record<
      string,
      {
        noItems?: true;
        hasDescriptors?: true;
        noAssignments?: true;
      }
    >;
    firstTarget: PublishViolationTarget;
  };

  export function asInfo(raw: Omit<RawInfo, "events"> & { events?: RawInfo["events"] }): Info {
    const plates = raw.plates.map((plate) => ({
      ...plate,
      nutrientTargets:
        plate.nutrientTargets === null ? null : Nutrient.normalizeTargets(plate.nutrientTargets),
      items: plate.items.flatMap((item) => {
        const normalized = Food.asItem(item);
        return normalized
          ? [
              {
                ...normalized,
                id: item.id,
                foodServingId: item.foodServingId,
                sortIndex: item.sortIndex,
              },
            ]
          : [];
      }),
    }));
    const events = [...(raw.events ?? [])];
    const assignments = events.flatMap((event) => event.assignments);

    return { ...raw, plates, events, assignments };
  }

  export async function fetchById(tx: Transaction, mealId: string): Promise<Info | null> {
    const raw = await tx.run(withRelated(zql.plannedMealV2.where("id", "=", mealId)));
    return raw ? asInfo(raw) : null;
  }

  export async function fetchByMealPlanningSessionId(
    tx: Transaction,
    mealPlanningSessionId: string,
  ): Promise<Info[]> {
    const rows = await listRowsByMealPlanningSessionId(tx, mealPlanningSessionId);
    return (await Promise.all(rows.map((row) => fetchById(tx, row.id)))).filter(
      (meal): meal is Info => meal !== null,
    );
  }

  export async function listIdsByMealPlanningSessionId(
    tx: Transaction,
    mealPlanningSessionId: string,
  ): Promise<string[]> {
    return (await listRowsByMealPlanningSessionId(tx, mealPlanningSessionId)).map((row) => row.id);
  }

  async function listRowsByMealPlanningSessionId(
    tx: Transaction,
    mealPlanningSessionId: string,
  ): Promise<Pick<MealRow, "id">[]> {
    const rows = await tx.run(
      zql.mealPlanningSessionPlannedMeal.where("mealPlanningSessionId", "=", mealPlanningSessionId),
    );
    return rows.map(({ plannedMealId }) => ({ id: plannedMealId }));
  }

  async function publishDraft(
    tx: Transaction,
    args: { id: string; timeNow: Temporal.Instant },
  ): Promise<void> {
    const meal = await requireLockedInfo(tx, args.id);
    if (meal.status !== "draft") {
      throw new Error("Only draft planned meals can be published");
    }

    await publishLoadedDraftInternal(tx, { meal, timeNow: args.timeNow });
  }

  async function publishLoadedDraftInternal(
    tx: Transaction,
    args: { meal: Info; timeNow: Temporal.Instant },
  ): Promise<void> {
    const violations = publishViolations(args.meal);
    if (violations?.firstTarget.code === "noPlates") {
      throw new Error("Planned meal requires at least one plate before publish");
    }
    if (violations?.firstTarget.code === "emptyName") {
      throw new Error("Planned meal requires a non-empty name before publish");
    }
    if (violations?.firstTarget.code === "noItems") {
      throw new Error("Each planned meal plate requires at least one item before publish");
    }
    if (violations?.firstTarget.code === "hasDescriptors") {
      throw new Error("Planned meal plates cannot include descriptors when publishing");
    }
    if (violations?.firstTarget.code === "noEvents") {
      throw new Error("Planned meal requires at least one event before publish");
    }
    if (violations?.firstTarget.code === "noAssignments") {
      throw new Error("Planned meal events and plates require assignments before publish");
    }

    await grantPublishedFoodAccess(tx, args.meal.plates);
    await tx.mutate.plannedMealV2.update({
      id: args.meal.id,
      status: "published",
      timeUpdated: args.timeNow,
    });
  }

  async function requireLockedInfo(tx: Transaction, mealId: string): Promise<Info> {
    await lockPlannedMeal(tx, mealId, "exclusive");
    const meal = await fetchById(tx, mealId);
    if (!meal) {
      throw new Error("Planned meal not found");
    }
    return meal;
  }

  export function publishViolations(meal: Info): PublishViolations | null {
    const violations: Omit<PublishViolations, "firstTarget"> = {
      plannedMeal: {},
      events: {},
      plates: {},
    };
    let firstTarget: PublishViolationTarget | undefined = undefined;

    const setFirstTarget = (target: PublishViolationTarget) => {
      firstTarget ??= target;
    };

    if (meal.plates.length === 0) {
      violations.plannedMeal.noPlates = true;
      setFirstTarget({ entity: "plannedMeal", code: "noPlates" });
    }

    if (meal.name.trim().length === 0) {
      violations.plannedMeal.emptyName = true;
      setFirstTarget({ entity: "plannedMeal", code: "emptyName" });
    }

    const assignedPlateIds = new Set(meal.assignments.map((assignment) => assignment.plateId));
    const eventAssignmentsByEventId = new Map<string, number>();
    for (const assignment of meal.assignments) {
      eventAssignmentsByEventId.set(
        assignment.eventId,
        (eventAssignmentsByEventId.get(assignment.eventId) ?? 0) + 1,
      );
    }

    for (const event of meal.events) {
      if ((eventAssignmentsByEventId.get(event.id) ?? 0) === 0) {
        violations.events[event.id] = { noAssignments: true };
        setFirstTarget({ entity: "event", id: event.id, code: "noAssignments" });
      }
    }

    for (const plate of meal.plates) {
      const plateViolations: PublishViolations["plates"][string] = {};
      const isAssigned = assignedPlateIds.has(plate.id);
      if (plate.items.length === 0) {
        plateViolations.noItems = true;
        setFirstTarget({ entity: "plate", id: plate.id, code: "noItems" });
      }
      if (plate.itemDescriptors.length > 0) {
        plateViolations.hasDescriptors = true;
        setFirstTarget({ entity: "plate", id: plate.id, code: "hasDescriptors" });
      }
      if (!isAssigned) {
        plateViolations.noAssignments = true;
        setFirstTarget({ entity: "plate", id: plate.id, code: "noAssignments" });
      }
      if (Object.keys(plateViolations).length > 0) {
        violations.plates[plate.id] = plateViolations;
      }
    }

    if (meal.events.length === 0) {
      violations.plannedMeal.noEvents = true;
      setFirstTarget({ entity: "plannedMeal", code: "noEvents" });
    }

    if (!firstTarget) {
      return null;
    }

    return {
      ...violations,
      firstTarget,
    };
  }

  async function lockPlannedMeal(tx: Transaction, mealId: string, mode: "shared" | "exclusive") {
    await acquireAdvisoryLockIfServer(tx, "plannedMeal", mealId, mode);
  }

  function assertUniqueIdsByLabel(label: string, ids: readonly string[]): void {
    const seenIds = new Set<string>();
    for (const id of ids) {
      if (seenIds.has(id)) {
        throw new Error(`Duplicate ${label} ids are not allowed`);
      }
      seenIds.add(id);
    }
  }

  function validatePlateState(plates: readonly EditablePlate[]) {
    const seenIds = new Set<string>();

    for (const plate of plates) {
      if (seenIds.has(plate.id)) {
        throw new Error("Plate ids must be unique within a planned meal");
      }
      seenIds.add(plate.id);

      const seenServingIds = new Set<string>();
      for (const item of plate.items) {
        if (seenServingIds.has(item.foodServingId)) {
          throw new Error("Each planned meal plate can only include a food serving once");
        }
        seenServingIds.add(item.foodServingId);
      }
    }
  }

  async function requireDraftCreateContext(
    tx: Transaction,
    actor: UserActor,
    scopeId: string,
  ): Promise<PlannedMealDraftContext> {
    if (tx.location === "client") {
      return { kind: "manual", location: "client" };
    }
    await ScopePolicy.requireWritable(tx, actor, scopeId, ["user", "space"]);
    const scope = await Scope.byId(tx, scopeId, ["user", "space"]);
    return { kind: "manual", location: "server", scope };
  }

  async function assertPlateUsersAllowed(
    tx: Transaction,
    context: PlannedMealDraftContext,
    userIds: readonly string[],
  ): Promise<void> {
    const uniqueUserIds = [...new Set(userIds)];
    if (context.location === "client") {
      return;
    }
    if (context.kind === "mealPlanningSession") {
      for (const userId of uniqueUserIds) {
        if (!(await Group.isMember(tx, userId, context.mealPlanningSessionId))) {
          throw new Error("Session-origin plates must target session members");
        }
      }
      return;
    }
    if (context.kind === "allowedUsers") {
      const allowedUserIds = new Set(context.userIds);
      if (uniqueUserIds.some((userId) => !allowedUserIds.has(userId))) {
        throw new Error(context.message);
      }
      return;
    }

    const { scope } = context;
    if (scope.kind === "user") {
      if (uniqueUserIds.some((userId) => userId !== scope.userId)) {
        throw new Error("Personal scopes can only target the owner user");
      }
      return;
    }

    for (const userId of uniqueUserIds) {
      if (!(await Group.isMember(tx, userId, scope.groupId))) {
        throw new Error("Shared scope plates must target current group members");
      }
    }
  }

  async function assertPlateServingReferencesViewable(
    tx: Transaction,
    actor: UserActor,
    plates: readonly Pick<EditablePlate, "items">[],
  ): Promise<void> {
    const servingIds = plates.flatMap((plate) => plate.items.map((item) => item.foodServingId));
    const canViewServings = await Food.canViewByServingIdBatch(tx, actor, servingIds);
    if (!canViewServings) {
      accessDenied("Cannot view food on planned meal plate item");
    }
  }

  async function grantPublishedFoodAccess(
    tx: Transaction,
    plates: readonly Plate[],
  ): Promise<void> {
    const grants = new Map<string, { foodId: string; userId: string }>();
    for (const plate of plates) {
      const servings = await tx.run(
        zql.foodServing.where(
          "id",
          "IN",
          plate.items.map((item) => item.foodServingId),
        ),
      );
      for (const serving of servings) {
        grants.set(`${serving.foodId}:${plate.userId}`, {
          foodId: serving.foodId,
          userId: plate.userId,
        });
      }
    }

    await Promise.all(
      [...grants.values()].map(async ({ foodId, userId }) => {
        if (await Food.canView(tx, { type: "user", userId }, foodId)) {
          return;
        }
        const existing = await tx.run(
          zql.accessGrant
            .where("resourceId", "=", foodId)
            .where("resourceType", "=", "food")
            .where("resourceScope", "=", "root")
            .where("granteeType", "=", "user")
            .where("granteeId", "=", userId)
            .one(),
        );
        if (existing) {
          return;
        }
        await AccessGrant.add(tx, AccessGrant.assignment(foodId, "food", "root", "viewer"), {
          type: "user",
          id: userId,
        });
      }),
    );
  }

  function normalizePlate(input: z.infer<typeof plateSchemas.add>): EditablePlate {
    return {
      id: input.id,
      userId: input.userId,
      nutrientTargets:
        input.nutrientTargets === null ? null : Nutrient.normalizeTargets(input.nutrientTargets),
      items: input.items.map((item) => ({
        id: item.id,
        foodServingId: item.foodServingId,
        amount: item.amount,
        sortIndex: item.sortIndex,
      })),
      itemDescriptors: input.itemDescriptors.map((descriptor) => ({
        id: descriptor.id,
        data: descriptor.data,
        sortIndex: descriptor.sortIndex,
      })),
    };
  }

  function toEditablePlate(
    plate: Pick<EditablePlate, "id" | "userId" | "nutrientTargets"> & {
      items: readonly Pick<
        EditablePlate["items"][number],
        "id" | "foodServingId" | "amount" | "sortIndex"
      >[];
      itemDescriptors: readonly Pick<
        EditablePlate["itemDescriptors"][number],
        "id" | "data" | "sortIndex"
      >[];
    },
  ): EditablePlate {
    return {
      id: plate.id,
      userId: plate.userId,
      nutrientTargets:
        plate.nutrientTargets === null ? null : Nutrient.normalizeTargets(plate.nutrientTargets),
      items: plate.items.map((item) => ({
        id: item.id,
        foodServingId: item.foodServingId,
        amount: item.amount,
        sortIndex: item.sortIndex,
      })),
      itemDescriptors: plate.itemDescriptors.map((descriptor) => ({
        id: descriptor.id,
        data: descriptor.data,
        sortIndex: descriptor.sortIndex,
      })),
    };
  }

  async function validatePlatesForMeal(
    tx: Transaction,
    actor: UserActor,
    context: PlannedMealDraftContext,
    plates: readonly EditablePlate[],
  ): Promise<void> {
    validatePlateState(plates);
    await assertPlateUsersAllowed(
      tx,
      context,
      plates.map((plate) => plate.userId),
    );
    await assertPlateServingReferencesViewable(tx, actor, plates);
  }

  function validateEventAssignmentChanges(
    info: Info,
    changes: NonNullable<z.infer<typeof editDraft.schema>["events"]>,
  ): void {
    const assignmentAdds = [
      ...(changes.add ?? []).flatMap((event) => event.assignments ?? []),
      ...(changes.edit ?? []).flatMap((event) => event.assignments?.add ?? []),
    ];
    const assignmentRemoves = (changes.edit ?? []).flatMap(
      (event) => event.assignments?.remove ?? [],
    );
    assertValidIdChanges(
      info.assignments,
      { add: assignmentAdds, remove: assignmentRemoves },
      {
        operation: "PlannedMeal.editDraft",
        label: "meal event assignment",
      },
    );

    const eventsById = mapById(info.events);
    for (const eventEdit of changes.edit ?? []) {
      const event = must(
        eventsById.get(eventEdit.id),
        "PlannedMeal.editDraft cannot edit missing meal event",
      );
      assertValidIdChanges(event.assignments, eventEdit.assignments, {
        operation: "PlannedMeal.editDraft",
        label: "meal event assignment",
      });
    }
  }

  function projectPlateChange(
    info: Info,
    plateId: string,
    plateEdit: z.infer<typeof plateSchemas.edit>,
  ): { plates: EditablePlate[]; current: EditablePlate; nextPlate: EditablePlate } {
    const plates = info.plates.map(toEditablePlate);
    const current = must(
      mapById(plates).get(plateId),
      "Planned meal plate not found on planned meal",
    );
    return { plates, current, nextPlate: projectPlateEdit(current, plateEdit) };
  }

  function projectPlateEdit(
    plate: EditablePlate,
    plateEdit: z.infer<typeof plateSchemas.edit>,
  ): EditablePlate {
    assertValidIdChanges(plate.items, plateEdit.items, {
      operation: "PlannedMeal.editDraft",
      label: "plate item",
    });
    assertValidIdChanges(plate.itemDescriptors, plateEdit.itemDescriptors, {
      operation: "PlannedMeal.editDraft",
      label: "plate item descriptor",
    });

    const removedItemIds = new Set(plateEdit.items?.remove?.map((entry) => entry.id) ?? []);
    const keptItems = plate.items.filter((item) => !removedItemIds.has(item.id));
    const nextItemsById = mapById(keptItems);
    for (const itemEdit of plateEdit.items?.edit ?? []) {
      const item = must(
        nextItemsById.get(itemEdit.id),
        "PlannedMeal.editDraft cannot edit missing plate item",
      );
      nextItemsById.set(item.id, {
        ...item,
        ...(itemEdit.foodServingId ? { foodServingId: itemEdit.foodServingId } : {}),
        ...(itemEdit.amount !== undefined ? { amount: itemEdit.amount } : {}),
        ...(itemEdit.sortIndex ? { sortIndex: itemEdit.sortIndex } : {}),
      });
    }

    const removedDescriptorIds = new Set(
      plateEdit.itemDescriptors?.remove?.map((entry) => entry.id) ?? [],
    );
    const keptDescriptors = plate.itemDescriptors.filter(
      (descriptor) => !removedDescriptorIds.has(descriptor.id),
    );
    const nextDescriptorsById = mapById(keptDescriptors);
    for (const descriptorEdit of plateEdit.itemDescriptors?.edit ?? []) {
      const descriptor = must(
        nextDescriptorsById.get(descriptorEdit.id),
        "PlannedMeal.editDraft cannot edit missing plate item descriptor",
      );
      nextDescriptorsById.set(descriptor.id, {
        ...descriptor,
        ...(descriptorEdit.data ? { data: descriptorEdit.data } : {}),
        ...(descriptorEdit.sortIndex ? { sortIndex: descriptorEdit.sortIndex } : {}),
      });
    }

    return {
      ...plate,
      nutrientTargets:
        plateEdit.nutrientTargets !== undefined
          ? plateEdit.nutrientTargets === null
            ? null
            : Nutrient.normalizeTargets(plateEdit.nutrientTargets)
          : plate.nutrientTargets,
      items: [
        ...keptItems.map((item) => nextItemsById.get(item.id) ?? item),
        ...(plateEdit.items?.add ?? []).map((item) => ({
          id: item.id,
          foodServingId: item.foodServingId,
          amount: item.amount,
          sortIndex: item.sortIndex,
        })),
      ],
      itemDescriptors: [
        ...keptDescriptors.map(
          (descriptor) => nextDescriptorsById.get(descriptor.id) ?? descriptor,
        ),
        ...(plateEdit.itemDescriptors?.add ?? []).map((descriptor) => ({
          id: descriptor.id,
          data: descriptor.data,
          sortIndex: descriptor.sortIndex,
        })),
      ],
    };
  }

  async function addPlateInternal(
    tx: Transaction,
    mealId: string,
    plate: EditablePlate,
    timeNow: Temporal.Instant,
  ): Promise<void> {
    await tx.mutate.plannedMealPlateV2.insert({
      id: plate.id,
      mealId,
      userId: plate.userId,
      nutrientTargets:
        plate.nutrientTargets === null ? null : Nutrient.normalizeTargets(plate.nutrientTargets),
    });

    await Promise.all(
      plate.items.map((item) =>
        tx.mutate.plannedMealPlateItemV2.insert({
          id: item.id,
          mealId,
          plateId: plate.id,
          foodServingId: item.foodServingId,
          amount: item.amount,
          sortIndex: item.sortIndex,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        }),
      ),
    );

    await Promise.all(
      plate.itemDescriptors.map((descriptor) =>
        tx.mutate.plannedMealPlateItemDescriptorV2.insert({
          id: descriptor.id,
          mealId,
          plateId: plate.id,
          data: descriptor.data,
          sortIndex: descriptor.sortIndex,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        }),
      ),
    );
  }

  async function editPlateInternal(
    tx: Transaction,
    mealId: string,
    plate: EditablePlate,
    plateEdit: z.infer<typeof plateSchemas.edit>,
    timeNow: Temporal.Instant,
  ): Promise<void> {
    if (plateEdit.nutrientTargets !== undefined) {
      await tx.mutate.plannedMealPlateV2.update({
        id: plate.id,
        nutrientTargets:
          plateEdit.nutrientTargets === null
            ? null
            : Nutrient.normalizeTargets(plateEdit.nutrientTargets),
      });
    }
    await Promise.all(
      (plateEdit.items?.remove ?? []).map(({ id }) => removePlateItemInternal(tx, id)),
    );
    await Promise.all(
      (plateEdit.items?.edit ?? []).map((itemEdit) => editPlateItemInternal(tx, itemEdit, timeNow)),
    );
    await Promise.all(
      (plateEdit.items?.add ?? []).map((itemAdd) =>
        addPlateItemInternal(tx, mealId, plate.id, itemAdd, timeNow),
      ),
    );
    await Promise.all(
      (plateEdit.itemDescriptors?.remove ?? []).map(({ id }) =>
        removePlateItemDescriptorInternal(tx, id),
      ),
    );
    await Promise.all(
      (plateEdit.itemDescriptors?.edit ?? []).map((descriptorEdit) =>
        editPlateItemDescriptorInternal(tx, descriptorEdit, timeNow),
      ),
    );
    await Promise.all(
      (plateEdit.itemDescriptors?.add ?? []).map((descriptorAdd) =>
        addPlateItemDescriptorInternal(tx, mealId, plate.id, descriptorAdd, timeNow),
      ),
    );
  }

  async function removePlateInternal(tx: Transaction, plate: EditablePlate): Promise<void> {
    const assignments = await tx.run(
      zql.plannedMealEventAssignmentV2.where("plateId", "=", plate.id),
    );
    await Promise.all(assignments.map((assignment) => removeAssignmentInternal(tx, assignment)));
    await Promise.all(plate.items.map(({ id }) => removePlateItemInternal(tx, id)));
    await Promise.all(
      plate.itemDescriptors.map(({ id }) => removePlateItemDescriptorInternal(tx, id)),
    );
    await tx.mutate.plannedMealPlateV2.delete({ id: plate.id });
  }

  async function addEventInternal(
    tx: Transaction,
    mealId: string,
    event: z.infer<typeof eventSchemas.add>,
    timeNow: Temporal.Instant,
  ): Promise<void> {
    await tx.mutate.plannedMealEventV2.insert({
      id: event.id,
      mealId,
      timeAt: event.timeAt,
      timeZone: event.timeZone,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
  }

  async function editEventInternal(
    tx: Transaction,
    _current: Event,
    event: z.infer<typeof eventSchemas.edit>,
    timeNow: Temporal.Instant,
  ): Promise<void> {
    const { assignments: _assignments, ...eventUpdate } = event;
    await tx.mutate.plannedMealEventV2.update({ ...eventUpdate, timeUpdated: timeNow });
  }

  async function editEventAssignmentsInternal(
    tx: Transaction,
    mealId: string,
    event: Pick<Event, "id" | "assignments">,
    eventEdit: z.infer<typeof eventSchemas.edit>,
    plates: readonly Pick<EditablePlate, "id" | "userId">[],
    timeNow: Temporal.Instant,
  ): Promise<void> {
    if (!eventEdit.assignments) {
      return;
    }

    const assignmentsById = mapById(event.assignments);
    await Promise.all(
      (eventEdit.assignments.remove ?? []).map(({ id }) =>
        removeAssignmentInternal(
          tx,
          must(assignmentsById.get(id), "PlannedMeal.editEvent cannot remove missing assignment"),
        ),
      ),
    );
    await Promise.all(
      (eventEdit.assignments.add ?? []).map((assignment) =>
        addAssignmentInternal(tx, mealId, { ...assignment, eventId: event.id }, plates, timeNow),
      ),
    );
  }

  async function removeEventInternal(tx: Transaction, event: Pick<Event, "id">) {
    const assignments = await tx.run(
      zql.plannedMealEventAssignmentV2.where("eventId", "=", event.id),
    );
    await Promise.all(assignments.map((assignment) => removeAssignmentInternal(tx, assignment)));
    await tx.mutate.plannedMealEventV2.delete({ id: event.id });
  }

  async function addAssignmentInternal(
    tx: Transaction,
    mealId: string,
    assignment: z.infer<typeof assignmentSchemas.add> & { eventId: string },
    plates: readonly Pick<EditablePlate, "id" | "userId">[],
    timeNow: Temporal.Instant,
  ): Promise<void> {
    const plate = must(
      mapById(plates).get(assignment.plateId),
      "PlannedMeal.addAssignment cannot assign a missing plate",
    );
    await tx.mutate.plannedMealEventAssignmentV2.insert({
      id: assignment.id,
      mealId,
      eventId: assignment.eventId,
      plateId: assignment.plateId,
      userId: plate.userId,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
  }

  async function removeAssignmentInternal(
    tx: Transaction,
    assignment: Pick<Assignment, "id">,
  ): Promise<void> {
    await tx.mutate.plannedMealEventAssignmentV2.delete({ id: assignment.id });
  }

  async function addPlateItemInternal(
    tx: Transaction,
    mealId: string,
    plateId: string,
    item: z.infer<typeof plateItemSchemas.add>,
    timeNow: Temporal.Instant,
  ): Promise<void> {
    await tx.mutate.plannedMealPlateItemV2.insert({
      id: item.id,
      mealId,
      plateId,
      foodServingId: item.foodServingId,
      amount: item.amount,
      sortIndex: item.sortIndex,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
  }

  async function editPlateItemInternal(
    tx: Transaction,
    item: z.infer<typeof plateItemSchemas.edit>,
    timeNow: Temporal.Instant,
  ): Promise<void> {
    await tx.mutate.plannedMealPlateItemV2.update({ ...item, timeUpdated: timeNow });
  }

  async function removePlateItemInternal(tx: Transaction, id: string): Promise<void> {
    await tx.mutate.plannedMealPlateItemV2.delete({ id });
  }

  async function addPlateItemDescriptorInternal(
    tx: Transaction,
    mealId: string,
    plateId: string,
    descriptor: z.infer<typeof plateItemDescriptorSchemas.add>,
    timeNow: Temporal.Instant,
  ): Promise<void> {
    await tx.mutate.plannedMealPlateItemDescriptorV2.insert({
      id: descriptor.id,
      mealId,
      plateId,
      data: descriptor.data,
      sortIndex: descriptor.sortIndex,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
  }

  async function editPlateItemDescriptorInternal(
    tx: Transaction,
    descriptor: z.infer<typeof plateItemDescriptorSchemas.edit>,
    timeNow: Temporal.Instant,
  ): Promise<void> {
    await tx.mutate.plannedMealPlateItemDescriptorV2.update({
      ...descriptor,
      timeUpdated: timeNow,
    });
  }

  async function removePlateItemDescriptorInternal(tx: Transaction, id: string): Promise<void> {
    await tx.mutate.plannedMealPlateItemDescriptorV2.delete({ id });
  }

  async function touchPlannedMeal(
    tx: Transaction,
    mealId: string,
    timeNow: Temporal.Instant,
  ): Promise<void> {
    await tx.mutate.plannedMealV2.update({ id: mealId, timeUpdated: timeNow });
  }

  function mapById<T extends { id: string }>(items: readonly T[]) {
    return new Map(items.map((item) => [item.id, item]));
  }

  async function requireWritableMeal(
    tx: Transaction,
    actor: UserActor,
    mealId: string,
  ): Promise<{ meal: MealRow; access: PlannedMealDraftContext | null }> {
    const initialMeal = await tx.run(zql.plannedMealV2.where("id", "=", mealId).one());
    if (!initialMeal) {
      throw new Error("Planned meal not found");
    }
    await lockPlannedMeal(tx, mealId, "exclusive");
    const meal = await tx.run(zql.plannedMealV2.where("id", "=", mealId).one());
    if (!meal) {
      throw new Error("Planned meal not found");
    }

    const scope = await writableParentScope(tx, actor, meal.scopeId);
    if (scope) {
      return {
        meal,
        access:
          meal.status === "draft"
            ? tx.location === "server"
              ? { kind: "manual", location: "server", scope }
              : { kind: "manual", location: "client" }
            : null,
      };
    }
    accessDenied("Cannot manage resources for this planned meal");
  }

  async function writableParentScope(
    tx: Transaction,
    actor: UserActor,
    scopeId: string,
  ): Promise<ParentScope | null> {
    try {
      await ScopePolicy.requireWritable(tx, actor, scopeId, ["user", "space"]);
      if (tx.location === "client") return null;
      return Scope.byId(tx, scopeId, ["user", "space"]);
    } catch (error) {
      if (error instanceof AccessDeniedError) {
        return null;
      }
      throw error;
    }
  }

  async function requireWritableDraftWithInfo(tx: Transaction, actor: UserActor, mealId: string) {
    const initialMeal = await tx.run(zql.plannedMealV2.where("id", "=", mealId).one());
    if (!initialMeal) {
      throw new Error("Planned meal not found");
    }
    await lockPlannedMeal(tx, mealId, "exclusive");
    const meal = await tx.run(zql.plannedMealV2.where("id", "=", mealId).one());
    if (!meal) {
      throw new Error("Planned meal not found");
    }
    if (meal.status !== "draft") {
      throw new Error("Only draft planned meals can be edited");
    }
    const access = await requireDraftAccessForMeal(tx, actor, meal);
    const info = await fetchById(tx, mealId);
    if (!info) {
      throw new Error("Planned meal not found");
    }
    return { access, info };
  }

  async function requireDraftAccessForMeal(
    tx: Transaction,
    actor: UserActor,
    meal: MealRow,
  ): Promise<PlannedMealDraftContext> {
    if (tx.location === "client") {
      return { kind: "manual", location: "client" };
    }
    const mealPlanningSessionId =
      await MealPlanningSessionScopePolicy.editablePlannedMealDraftSessionId(tx, actor, {
        plannedMealId: meal.id,
        scopeId: meal.scopeId,
      });
    if (mealPlanningSessionId) {
      return { kind: "mealPlanningSession", location: "server", mealPlanningSessionId };
    }
    await ScopePolicy.requireWritable(tx, actor, meal.scopeId, ["user", "space"]);
    const scope = await Scope.byId(tx, meal.scopeId, ["user", "space"]);
    return { kind: "manual", location: "server", scope };
  }
}
